import React, { createContext, useContext, useEffect } from 'react';
import TrackingService from '../services/TrackingService';
import { EventType, EventData } from '../types/events';

// Context type definition
interface TrackingContextType {
  // Add any methods or state that would be useful to expose to components
  trackEvent: (eventType: EventType | string, eventData?: Record<string, any>) => boolean;
  forceFlushEvents: () => Promise<void>;
}

// Create the context with default values
const TrackingContext = createContext<TrackingContextType>({
  trackEvent: () => false,
  forceFlushEvents: async () => {},
});

// Hook for consuming the context
export const useTracking = () => useContext(TrackingContext);

interface TrackingProviderProps {
  children: React.ReactNode;
}

// Provider component that wraps your app and makes event tracking available
export const TrackingProvider: React.FC<TrackingProviderProps> = ({ children }) => {
  // Set up event listeners for page visibility and unmount
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        TrackingService.forceFlush();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle window close/refresh
    const handleBeforeUnload = () => {
      TrackingService.forceFlush();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      TrackingService.forceFlush();
    };
  }, []);

  // Context value
  const contextValue: TrackingContextType = {
    trackEvent: (eventType, eventData = {}) => {
      return TrackingService.trackEvent(eventType, eventData);
    },
    forceFlushEvents: () => TrackingService.forceFlush(),
  };

  return <TrackingContext.Provider value={contextValue}>{children}</TrackingContext.Provider>;
};
